import {
  DownloadFileVersionsParams,
  FileToDownload,
} from '~src/utils/downloader/types/types';
import { appLog } from '../log';
import {
  filterOutLargeFiles,
  validateTotalSize,
  writeFilesToZipStream,
} from '~src/utils/downloader/shared';
import { DownloadingState, useStore } from '~src/store';
import * as zip from '@zip.js/zip.js';
import streamSaver from 'streamsaver';
import { Modal } from 'antd';

// --- ZipStream Class ---
class ZipStream {
  private zipWriter: zip.ZipWriter<WritableStream<any>> | null = null;
  private writableStream: WritableStream<any> | null = null;
  public successLog: string[] = [];
  public errorLog: string[] = [];
  private fileMap: Map<string, number> = new Map();
  private currentFileAlreadyStreamed = 0;
  private totalFiles = 0;
  public totalSize = 0;
  private downloadedSize = 0;
  private startTime = 0;
  private endTime = 0;
  public isCancelled = false;

  constructor(totalFiles: number, totalSize: number) {
    this.totalFiles = totalFiles;
    this.totalSize = totalSize;
    useStore.getState().changeTotalFileSize(totalSize);
  }

  start() {
    try {
      // console.log('ZIP stream started');
      useStore.getState().totalDownloadFiles(this.totalFiles);
      this.startTime = performance.now();
      this.writableStream = streamSaver.createWriteStream(
        `C-WEB-Project-${Date.now()}.zip`
      );
      this.zipWriter = new zip.ZipWriter(this.writableStream);
    } catch (error) {
      console.error('Error initializing ZIP stream:', error);
      this.isCancelled = true;
    }
  }

  cancel() {
    // console.log('ZIP download cancelled');
    this.isCancelled = true;
    this.writableStream = null;
    this.zipWriter = null;
    useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 3000);
  }

  getUniqueFileName(filePath: string): string {
    let uniquePath = filePath;
    let count = 1;
    const match = filePath.match(/^(.*?)(\(\d+\))?(\.[^/.]+)?$/);
    const baseName = match?.[1] || filePath;
    const extension = match?.[3] || '';
    while (this.fileMap.has(uniquePath)) {
      uniquePath = `${baseName}(${count})${extension}`;
      count++;
    }
    this.fileMap.set(uniquePath, count);
    return uniquePath;
  }

  async addFile(name: string, blob: Blob) {
    if (this.isCancelled || !this.zipWriter) return;
    const uniqueName = this.getUniqueFileName(name);
    try {
      useStore.getState().changeDownloadingState(DownloadingState.FETCHING);
      await this.zipWriter.add(uniqueName, new zip.BlobReader(blob));
      this.currentFileAlreadyStreamed++;
      this.downloadedSize += blob.size;
      useStore.getState().currentDownloadFile(this.currentFileAlreadyStreamed);
      this.successLog.push(
        `${uniqueName} - success (${(blob.size / 1024 / 1024).toFixed(2)} MB)`
      );
    } catch (error) {
      this.errorLog.push(
        `${uniqueName} (0.00 MB) - ERROR: ${error?.message} | D: ShareFiles`
      );
      useStore
        .getState()
        .addError(
          `${uniqueName} (0.00 MB) - ERROR: ${error?.message} | D: ShareFiles`
        );
      this.isCancelled = true;
    }
  }

  async finish() {
    try {
      if (!this.zipWriter || this.isCancelled) return;
      this.endTime = performance.now();
      const totalSizeMB = (this.downloadedSize / 1024 / 1024).toFixed(2);
      const timeTaken = (this.endTime - this.startTime) / 1000;
      const speed = (this.downloadedSize / 1024 / 1024 / timeTaken).toFixed(2);
      const totalErrors = this.errorLog.length;

      // Merge successes and errors in a structured way
      const mergedLogs: string[] = [];
      const successEntries = [...this.successLog];
      const errorEntries = [...this.errorLog];

      while (successEntries.length || errorEntries.length) {
        if (successEntries.length) mergedLogs.push(successEntries.shift()!);
        if (errorEntries.length) mergedLogs.push(errorEntries.shift()!);
      }

      // Construct final log content
      const finalLog = [
        ...mergedLogs,
        `\n==== Summary V2 ====`,
        `Total files - ${this.totalFiles}`,
        `Total errors - ${totalErrors}`,
        `Total size - ${totalSizeMB} MB`,
        `Download speed - ${speed} MB/s`,
        `Time taken - ${timeTaken.toFixed(2)} s`,
      ];

      if (totalErrors > 0) {
        finalLog.push(`\n==== ${totalErrors} Errors ====`, ...this.errorLog);
      }

      const logBlob = new Blob([finalLog.join('\n')], { type: 'text/plain' });
      await this.zipWriter.add('download_log.txt', new zip.BlobReader(logBlob));
      await this.zipWriter.close();

      const clearPanel = () => {
        useStore.getState().changeDownloadingActive(false);
        useStore.getState().clearErrors();
      };

      useStore.getState().changeDownloadingState(DownloadingState.FINISHING);
      // setTimeout(() => clearPanel(), 3000);
      // console.log('ZIP file successfully created.');
    } catch (error) {
      console.error('Error finishing ZIP stream:', error);
      this.isCancelled = true;
    }
  }
}

// --- Worker Creation ---
const createWorker = () => {
  return new Worker(
    URL.createObjectURL(
      new Blob(
        [
          `self.onmessage = async (event) => {
            const { fileUrl, fileName } = event.data;
            try {
              if (!fileUrl) {
                throw new Error("File URL is missing");
              }
              const response = await fetch(fileUrl);
              if (!response.ok) {
                throw new Error(\`HTTP \${response.status} - \${response.statusText}\`);
              }
              const blob = await response.blob();
              self.postMessage({ success: true, fileName, blob });
            } catch (error) {
              self.postMessage({ success: false, fileName, error: error.message });
            }
          };`,
        ],
        { type: 'application/javascript' }
      )
    )
  );
};

// --- Modal Confirm Download (unchanged) ---
const ModalConfirmDownload = (): Promise<boolean> => {
  function onCancelfunction() {
    useStore.getState().changeDownloadingState('FINISHING');
    setTimeout(() => useStore.getState().changeDownloadingActive(false), 1500);
  }
  return new Promise((resolve) => {
    Modal.confirm({
      title: 'Warning',
      content:
        'Too much files selected (50+ files). There can be problems with downloading. We recommend downloading to reduce total files selected to 50 max. at once.',
      onOk: () => resolve(true),
      onCancel: () => {
        onCancelfunction();
        resolve(false);
      },
      okText: 'I understand',
      cancelText: 'Cancel',
    });
  });
};

// splits an array into chunks of given size.
function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

export const downloadFileVersions = async ({
  fileVersions,
  onDownloadStarted,
  onLargeFiles,
}: DownloadFileVersionsParams): Promise<unknown> => {
  useStore.getState().changeDownloadingState(DownloadingState.DOWNLOADING);
  appLog('downloadFileVersions ->', { fileVersions });

  let startDownload = true;
  if (fileVersions.length > 50) {
    startDownload = await ModalConfirmDownload();
  }
  if (!startDownload) return;

  onDownloadStarted && onDownloadStarted();
  if (!fileVersions.length) return;

  const totalFiles = fileVersions.length;
  const chunkSize = 20;
  const Workers_Num = 5;
  const fileChunks = chunkArray(fileVersions, chunkSize);

  const zipStream = new ZipStream(totalFiles, 0);
  zipStream.start();
  if (zipStream.isCancelled) return;

  const workers: Worker[] = [];
  for (let i = 0; i < Workers_Num; i++) {
    workers.push(createWorker());
  }

  let processedFiles = 0;
  let downloadedSize = 0;

  for (const chunk of fileChunks) {
    await new Promise<void>((resolve) => {
      let activeWorkers = 0;
      let remainingFiles = chunk.length;

      const processFile = (file, worker: Worker) => {
        if (!file.location) {
          zipStream.errorLog.push(
            `${file.name} (0.00 MB) - ERROR: Missing file location | D: ShareFiles`
          );
          useStore
            .getState()
            .addError(
              `${file.name} (0.00 MB) - ERROR: Missing file location | D: ShareFiles`
            );
          remainingFiles--;
          if (remainingFiles === 0 && activeWorkers === 0) resolve(undefined);
          return;
        }
        activeWorkers++;
        worker.postMessage({
          fileUrl: file.location,
          fileName: file.name,
          originalFile: file,
        });

        worker.onmessage = async (event) => {
          const { success, fileName, blob, error } = event.data;
          if (success) {
            await zipStream.addFile(fileName, blob);
            downloadedSize += blob.size;
            processedFiles++;
          } else {
            zipStream.errorLog.push(
              `${fileName} (0.00 MB) - ERROR: ${error} | D: ShareFiles`
            );
            useStore
              .getState()
              .addError(
                `${fileName} (0.00 MB) - ERROR: ${error} | D: ShareFiles`
              );
          }

          activeWorkers--;
          remainingFiles--;
          if (remainingFiles === 0 && activeWorkers === 0) resolve(undefined);
        };
      };

      chunk.forEach((file, index) => {
        processFile(file, workers[index % Workers_Num]);
      });
    });
  }

  workers.forEach((worker) => worker.terminate());
  await zipStream.finish();
};
